import { useState, useMemo, useEffect } from 'react';
import cm from 'classnames';

import { Story } from 'services/story.service';
import { ObjectType } from 'services/access.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { isNull, prettyDate } from 'components/app/_helpers/_app.helper';
import {
  copyStory,
  createStory,
  fetchStories,
  updateEntity,
  setRemovingInstance,
  selectStory,
} from 'components/app/_mediator/app-data.slice';

import { useCreateStory } from '../use-create-story';
import { setStoryModal } from 'components/app/_mediator/app.slice';
import { fetchStoryConfig } from 'components/app/editor/_mediator/editor.slice';
import { sortProjectEntities } from '../_mediator/projects.helper';
import { setSelectedStory, setAccessTypeModal } from '../_mediator/projects.slice';
import { getStoriesSortCb, PROJECTS_STORIES_HEADERS } from '../_mediator/projects.params';
import { useProjectsSearch } from '../use-projects-search';

import { ReactComponent as MoveToIcon } from 'assets/ui/move-to.svg';
import { ReactComponent as TrashIcon } from 'assets/ui/trash.svg';
import { ReactComponent as PlusIcon } from 'assets/ui/plus.svg';

import { EditCell } from 'components/app/app-components/edit-cell/edit-cell';
import { PlaceholderStories } from '../placeholders/placeholder-stories';

import { HeaderAction } from '../elements/header-action';
import { HeaderSearch } from '../elements/header-search';
import { HeaderAccessIco } from '../elements/header-access-ico';
import { CreateElementModal } from '../elements/modal-create-element';
import { StoryMenuContainer } from '../elements/story-menu';
import { HeaderActionFolder } from '../elements/header-action-folders';

import { ProjectsTable } from './elements/projects-table';
import { SectionHeader } from './elements/section-header';
import { Section } from './elements/section';

import styles from './_sections.module.scss';

export const SectionStories = () => {
  const { stories, projects, removingInstance } = useAppSelector((state) => state.app__data);
  const { selectedStory, selectedProject, storiesTableState, storiesTableSort } = useAppSelector(
    (state) => state.projects
  );
  const storyData = useAppSelector(selectStory);
  const { createStoryDisabled, createStoryTooltip } = useCreateStory();
  const dispatch = useAppDispatch();

  const [storyName, setStoryName] = useState('');
  const [storyCreating, setStoryCreating] = useState(false);
  const [storyModalId, setStoryModalId] = useState<number | null>(null);

  const { searchValue, updateSearchValue, getFilteredList, clearSearchValue } =
    useProjectsSearch('storiesSearchFilter');

  const filteredStories = useMemo(() => (stories ? getFilteredList<Story>(stories) : []), [stories, searchValue]);

  const sortedStories = useMemo(() => {
    return sortProjectEntities(
      filteredStories,
      storiesTableSort.order,
      getStoriesSortCb(PROJECTS_STORIES_HEADERS[storiesTableSort.column])
    );
  }, [filteredStories, storiesTableSort]);

  const selectedProjectName = useMemo(
    () => projects?.find((project) => project.projectId === selectedProject)?.name,
    [projects, selectedProject]
  );

  const isDisabled = useMemo(() => {
    return !selectedStory || removingInstance?.instance !== 'story';
  }, [selectedStory, removingInstance?.instance]);

  const handleSelect = (id: number) => {
    dispatch(setRemovingInstance({ id: id, instance: 'story', modalOpen: false }));
    dispatch(setSelectedStory(id));
  };

  const handleCreate = async () => {
    if (!selectedProject || isNull(storyModalId)) return;
    setStoryCreating(true);
    (storyModalId
      ? dispatch(copyStory({ name: storyName, storyId: storyModalId })).unwrap()
      : dispatch(createStory({ projectId: selectedProject, name: storyName })).unwrap()
    )
      .then(async (story) => {
        if (!story) return;
        await dispatch(fetchStories(selectedProject));
        dispatch(setSelectedStory(story.storyId));

        // Open config modal for new story creation (not when copying)
        if (storyModalId === 0) {
          // Fetch story config before opening modal
          await dispatch(fetchStoryConfig(story.storyId));
          dispatch(setStoryModal('setup'));
        }
      })
      .finally(() => {
        clearSearchValue(storyName);
        setStoryName('');
        dispatch(setRemovingInstance(null));
        setStoryModalId(null);
        setStoryCreating(false);
      });
  };

  const handleCopy = (storyId: StoryId) => {
    if (!selectedProject) return;

    const storyName = stories?.find((story) => storyId === story.storyId)?.name;

    setStoryName(`${storyName} (copy)`);
    setStoryModalId(storyId);
  };

  const handleSettings = (id: StoryId) => {
    handleSelect(id);
    dispatch(setStoryModal());
  };

  useEffect(() => {
    return () => clearSearchValue();
  }, [selectedProject]);

  return (
    <Section expanded={storiesTableState}>
      <SectionHeader
        expanded={storiesTableState}
        keyTableState="storiesTableState"
        title="Stories"
        projectName={selectedProjectName}
        len={sortedStories.length}
        empty={!stories?.length}
        tip="You may have different stories (or versions) to tell about your project"
      >
        <HeaderSearch
          searchValue={searchValue}
          updateSearchValue={updateSearchValue}
          clearSearchValue={clearSearchValue}
        />
        <HeaderAction
          type="create"
          icon={<PlusIcon />}
          onSubmit={() => setStoryModalId(0)}
          data-intro={IntroKeys.createStory}
          disabled={createStoryDisabled}
          tip={createStoryTooltip}
        />
        <HeaderAction
          type="access"
          icon={<HeaderAccessIco accessType={storyData?.accessType} />}
          onSubmit={() => dispatch(setAccessTypeModal(ObjectType.Story))}
          disabled={!selectedProject}
          tip="Manage story access"
        />
        <HeaderAction
          type="delete"
          icon={<TrashIcon />}
          onSubmit={() => {
            removingInstance?.instance === 'story' &&
              dispatch(setRemovingInstance({ ...removingInstance, modalOpen: true }));
          }}
          disabled={isDisabled}
          tip={isDisabled ? 'Select a story for deletion' : 'Delete story'}
        />
        <HeaderActionFolder type="story" disabled={isDisabled}>
          <HeaderAction
            type="moveTo"
            icon={<MoveToIcon />}
            disabled={isDisabled}
            tip={isDisabled ? 'Select a story to move' : 'Move story to a folder'}
          />
        </HeaderActionFolder>
      </SectionHeader>
      <div className={styles.SectionBody}>
        <PlaceholderStories cb={() => setStoryModalId(0)} />
        {stories && stories.length !== 0 && (
          <ProjectsTable
            sortKey="storiesTableSort"
            headers={PROJECTS_STORIES_HEADERS}
            selectedRow={selectedStory ?? undefined}
          >
            {sortedStories?.map((row) => (
              <tr
                key={row.storyId}
                onClick={() => handleSelect(row.storyId)}
                data-testid="project-story"
                className={cm({
                  [styles.selected]: row.storyId === selectedStory,
                  [styles.removing]:
                    removingInstance?.instance === 'story' &&
                    removingInstance?.modalOpen &&
                    removingInstance?.id === row.storyId,
                })}
              >
                <EditCell
                  element={{ name: row.name, id: row.storyId }}
                  onSubmit={(element) =>
                    dispatch(updateEntity({ id: element.id, name: element.name, entity: 'stories' }))
                  }
                  selectedID={selectedStory}
                  selectedName={stories?.find((story) => story.storyId === selectedStory)?.name ?? ''}
                  width={13}
                />
                <td></td>
                <td>{prettyDate(row.createdAt)}</td>
                <td>{prettyDate(row.accessedAt)}</td>
                <td>{prettyDate(row.updatedAt)}</td>
                <td>
                  <StoryMenuContainer storyId={row.storyId} handleCopy={handleCopy} handleSettings={handleSettings}>
                    <div className={styles.controlBurger}>
                      <div />
                      <div />
                      <div />
                    </div>
                  </StoryMenuContainer>
                </td>
              </tr>
            ))}
          </ProjectsTable>
        )}
      </div>

      {/* modal */}
      <CreateElementModal
        isOpen={!isNull(storyModalId)}
        onClose={() => {
          setStoryModalId(null);
          setStoryName('');
        }}
        onSubmit={handleCreate}
        modalTitle="Name your story"
        inputValue={storyName}
        onInputChange={(value) => setStoryName(value)}
        disabled={!storyName || storyCreating}
        submitText={storyModalId ? 'Copy' : undefined}
      />
    </Section>
  );
};
